import React, { memo, useMemo, useState } from 'react'
import styles from './HardwareGrid.module.scss'
import { useHardwareFilter } from '../../filter'
import { DeviceTypeValues, deviceTypes, isDeviceType } from '../../data_types'
import {
  useBallAssignmentFunctions,
  usePlayerAssignmentFunctions,
  useFormattedSession
} from '../../../sessions/hooks'
import { Button } from '../../../../components/Button/Button'
import { Grid } from '@mui/material'
import { SearchableDropdown } from '../../../../components/SearchableDropdown/SearchableDropdown'
import BasicSelect from '../../../../components/Material/Select'
import { FormattedDevice, HardwareStates } from '../../types'
import { HardwareBall } from './HardwareItem/HardwareBall/HardwareBall'
import { HardwareTag } from './HardwareItem/HardwareTag/HardwareTag'
import { HardwareCommon } from './HardwareItem/HardwareCommon/HardwareCommon'
import { Toggle } from '../../../../components/Toggle/Toggle'
import { TagInstructionsCard } from '../../../../components/ExpandableInfoCard/ExpandableInfoCard'

export interface HardwareGridProps {
  deviceTypeValue: DeviceTypeValues
  hardwareStates?: HardwareStates[]
  teamId?: string
  isSessionAssignment?: boolean
  viewMode?: number
  setViewMode?: (value: number) => void
  selectionManager?: any
}

export const HardwareGrid = memo((props: HardwareGridProps) => {
  const {
    deviceTypeValue,
    isSessionAssignment = false,
    teamId,
    hardwareStates,
    viewMode = 0,
    setViewMode,
    selectionManager
  } = props

  // State for tag sorting option
  const [sortByAssigned, setSortByAssigned] = useState(false)

  const deviceType = deviceTypes.getTypeByValue(deviceTypeValue)
  // Always sort tags by serial number for both view modes
  const isPlayerTagType = isDeviceType.playerTag(deviceType)

  const { playersSessions, sport } = useFormattedSession('setup')

  const { filteredHardware, filters, updateFilterValue } = useHardwareFilter(
    {
      type: deviceType.value,
      team: teamId,
      status: hardwareStates
    },
    'setup'
  )

  const { addAllBallsToSession, removeAllBallsFromSession } =
    useBallAssignmentFunctions()
  const { randomTagAssign } = usePlayerAssignmentFunctions(teamId)

  const handleSearchChange = (event) => {
    updateFilterValue('searchStr', event)
  }

  const header = useMemo(() => {
    if (isSessionAssignment) {
      if (isDeviceType.playerTag(deviceType)) {
        return 'Assign Tags to Players'
      }
      if (isDeviceType.ball(deviceType)) {
        return 'Select balls to add to session'
      }
    }
    return null
  }, [isSessionAssignment, deviceType, viewMode])

  // Function to extract last two characters from serial
  const getLastTwoChars = (serial) => {
    if (!serial || typeof serial !== 'string') return ''
    return serial.slice(-2)
  }

  // Function to compare two serials by their last two digits
  const compareSerialsByLastTwoDigits = (serialA, serialB) => {
    const lastTwoA = getLastTwoChars(serialA)
    const lastTwoB = getLastTwoChars(serialB)
    return lastTwoA.localeCompare(lastTwoB)
  }

  // Get devices - sorted based on user preference
  const processedDevices = useMemo(() => {
    if (!filteredHardware.list || filteredHardware.list.length === 0) {
      return []
    }

    // Create a copy of the array to avoid mutating the original
    const sortedDevices = [...filteredHardware.list]

    // First sort by last two characters of serial if device type is player tag
    if (isPlayerTagType) {
      sortedDevices.sort((a, b) => {
        return compareSerialsByLastTwoDigits(a.serial, b.serial)
      })
    }

    // If sort by assigned is enabled, apply that sorting
    if (sortByAssigned && isPlayerTagType) {
      sortedDevices.sort((a, b) => {
        const aAssigned = playersSessions.byHardwareId.map[a.id] ? 1 : 0
        const bAssigned = playersSessions.byHardwareId.map[b.id] ? 1 : 0
        return aAssigned - bAssigned // Unassigned (0) will come before assigned (1)
      })
    }

    return sortedDevices
  }, [
    filteredHardware.list,
    playersSessions.byHardwareId.map,
    sortByAssigned,
    isPlayerTagType
  ])

  // Split devices into four columns for list view
  const deviceColumns = useMemo(() => {
    if (processedDevices.length === 0)
      return { column1: [], column2: [], column3: [], column4: [] }

    const columnSize = Math.ceil(processedDevices.length / 4)

    return {
      column1: processedDevices.slice(0, columnSize),
      column2: processedDevices.slice(columnSize, columnSize * 2),
      column3: processedDevices.slice(columnSize * 2, columnSize * 3),
      column4: processedDevices.slice(columnSize * 3)
    }
  }, [processedDevices])

  const getHardwareComponent = (device: FormattedDevice, isList = false) => {
    if (isDeviceType.ball(device.type)) {
      return (
        <HardwareBall
          device={device}
          isSessionAssignment={isSessionAssignment}
        />
      )
    } else if (isDeviceType.playerTag(device.type)) {
      return (
        <HardwareTag
          device={device}
          isSessionAssignment={isSessionAssignment}
          list={isList}
          selectionManager={
            isList && viewMode === 1 ? selectionManager : undefined
          }
        />
      )
    } else if (isDeviceType.anchor(device.type)) {
      return <HardwareCommon device={device} />
    } else {
      return <noscript />
    }
  }

  const renderDeviceInSessionS = (device) =>
    device.inSession && (
      <div className={styles.sessionIndicator}>
        <div>S</div>
      </div>
    )

  const toggleSortByAssigned = () => {
    setSortByAssigned(!sortByAssigned)
  }

  return (
    <div className={styles.hardwardGridContainer}>
      <div className={styles.hardwareFiltersContainer}>
        <div className={styles.headerRow}>
          {header && !sport.props.features.positionNumbers && (
            <h5 className={styles.title}>{header}</h5>
          )}
          {/* View toggle */}
          {isDeviceType.playerTag(deviceType) &&
            !sport.props.features.positionNumbers &&
            isSessionAssignment && (
              <div className={styles.viewToggleContainer}>
                <Toggle
                  input={{
                    value: viewMode,
                    onChange: (value: number) => {
                      if (selectionManager) {
                        selectionManager.clearSelection()
                      }
                      setViewMode(value)
                    }
                  }}
                  options={[
                    { name: 'Grid', value: 0 },
                    { name: 'List', value: 1 }
                  ]}
                  size={'s'}
                />
              </div>
            )}
        </div>

        {isSessionAssignment && (
          <Grid container justifyContent='space-between' alignItems='center'>
            <Grid item xs={2}>
              <BasicSelect
                label={'Search Tags'}
                variant='standard'
                selected={filters.searchStr.value}
                onChange={(e) => handleSearchChange(e)}
                labelShrink={true}
                size={'small'}
                fontSize={20}
              />
            </Grid>
            <Grid item xs={2}>
              <BasicSelect
                variant='standard'
                options={filters.assigned.options}
                onChange={(value) => updateFilterValue('assigned', value)}
                label={'Assigned'}
                selected={filters.assigned.value}
                size={'small'}
                fontSize={15}
              />
            </Grid>
            <Grid item xs={2}>
              <SearchableDropdown
                fontSize={15}
                items={filters.status.options}
                onSelect={(value) => updateFilterValue('status', value)}
                filterName={'status'}
                value={filters.status.value}
                label={'Status'}
                isMultiple={true}
                inputVariant='standard'
                labelShrink={true}
              />
            </Grid>
            {isDeviceType.playerTag(deviceType) && (
              <Grid item xs={2}>
                <Button
                  className={'btn--primary-thin'}
                  handleClick={randomTagAssign}
                  style={{ width: '100%' }}
                >
                  Quick Assign Tags
                </Button>
              </Grid>
            )}

            {/* Sort button - only for assigned/unassigned */}
            {isDeviceType.playerTag(deviceType) && (
              <Grid item xs={2}>
                <Button
                  className={
                    sortByAssigned ? 'btn--primary-thin' : 'btn--border-thin'
                  }
                  handleClick={toggleSortByAssigned}
                  style={{ width: '100%' }}
                >
                  {sortByAssigned ? 'Assigned Last ✓' : 'Sort By Assigned'}
                </Button>
              </Grid>
            )}
          </Grid>
        )}

        {isDeviceType.ball(deviceType) && isSessionAssignment && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ marginTop: '5px' }}>
              <Button
                id={'addAllBallsButton'}
                className={'btn--primary-thin'}
                handleClick={addAllBallsToSession}
                style={{ alignSelf: 'center' }}
              >
                Add All Balls
              </Button>
            </div>
            <div style={{ marginTop: '5px' }}>
              <Button
                className={'btn--border-thin'}
                handleClick={removeAllBallsFromSession}
                style={{ alignSelf: 'center' }}
              >
                Remove All Balls
              </Button>
            </div>
          </div>
        )}
      </div>

      {viewMode === 1 && isDeviceType.playerTag(deviceType) && (
        <TagInstructionsCard />
      )}

      {filteredHardware.count < 1 ? (
        <div className={styles.noHardware}>
          <h5>
            {isDeviceType.anchor(deviceType) && 'No anchors found...'}
            {isDeviceType.playerTag(deviceType) && 'No player tags found...'}
            {isDeviceType.ball(deviceType) && 'No balls found...'}
          </h5>
        </div>
      ) : viewMode === 0 || !isDeviceType.playerTag(deviceType) ? (
        /* Grid View - using processed devices (sorted or not based on user preference) */
        <div className={styles.hardwareScroller}>
          <div className={styles.hardwardGrid}>
            {processedDevices.map((device) => (
              <div className='relative' key={device.id}>
                {getHardwareComponent(device)}
                {renderDeviceInSessionS(device)}
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* List View - using processed devices (sorted or not based on user preference) */
        <div className={styles.hardwareScroller}>
          <div className={styles.hardwareListColumns}>
            {/* First column */}
            <div className={styles.hardwareListColumn}>
              {deviceColumns.column1.map((device) => (
                <div className='relative' key={device.id}>
                  {getHardwareComponent(device, true)}
                  {renderDeviceInSessionS(device)}
                </div>
              ))}
            </div>

            {/* Second column */}
            <div className={styles.hardwareListColumn}>
              {deviceColumns.column2.map((device) => (
                <div className='relative' key={device.id}>
                  {getHardwareComponent(device, true)}
                  {renderDeviceInSessionS(device)}
                </div>
              ))}
            </div>

            {/* Third column */}
            <div className={styles.hardwareListColumn}>
              {deviceColumns.column3.map((device) => (
                <div className='relative' key={device.id}>
                  {getHardwareComponent(device, true)}
                  {renderDeviceInSessionS(device)}
                </div>
              ))}
            </div>

            {/* Fourth column */}
            <div className={styles.hardwareListColumn}>
              {deviceColumns.column4.map((device) => (
                <div className='relative' key={device.id}>
                  {getHardwareComponent(device, true)}
                  {renderDeviceInSessionS(device)}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
})
